import {Modal, Select} from '@mantine/core';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {zodResolver} from '@hookform/resolvers/zod';
import React, {useMemo} from 'react';
import {Controller, useForm} from 'react-hook-form';
import type { SuperAdminResponse} from 'src/api/ProactApiClient';
import {RoleEnum} from 'src/api/ProactApiClient';
import {DeleteButton} from 'src/design/molecules/DeleteButton';
import {DialogButton} from 'src/design/molecules/DialogButton';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {useGetInstanceListQuery} from 'src/redux/queries/instance';
import {useDemoteSuperAdminMutation} from 'src/redux/queries/userInstance';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {z} from 'zod';

const ROLE_OPTIONS = [
  {value: String(RoleEnum.InstanceAdmin), label: 'Admin'},
  {value: String(RoleEnum.InstanceUser), label: 'User'},
  {value: String(RoleEnum.InstanceObserver), label: 'Observer'},
  {value: String(RoleEnum.InstanceSupervisor), label: 'Supervisor'},
  {value: String(RoleEnum.SafetyRepresentative), label: 'Safety Rep'},
  {value: String(RoleEnum.NotificationOnly), label: 'Notifications Only'},
];

const schema = z.object({
  targetInstanceId: z.string().min(1, 'Please select an instance.'),
  roleId: z.string().min(1, 'Please select a role.'),
});

type DemoteSuperAdminFormValues = z.infer<typeof schema>;

const defaultValues: DemoteSuperAdminFormValues = {
  targetInstanceId: '',
  roleId: '',
};

export interface DemoteSuperAdminModalProps {
  target: SuperAdminResponse | null;
  onClose: () => void;
  onDemoted?: () => void;
}

export const DemoteSuperAdminModal: React.FC<DemoteSuperAdminModalProps> = ({
  target,
  onClose,
  onDemoted,
}) => {
  const {setAlert} = useAlert();
  const [demoteSuperAdmin, {isLoading}] = useDemoteSuperAdminMutation();

  const {data: instanceListData} = useGetInstanceListQuery(
    {start: 0, length: 1000},
    {skip: target === null || target.hasInstanceMembership}
  );

  const instanceOptions = useMemo(
    () =>
      (instanceListData?.data ?? []).map(inst => ({
        value: String(inst.id),
        label: inst.name,
      })),
    [instanceListData]
  );

  const {
    control,
    handleSubmit: handleFormSubmit,
    reset,
    formState: {isValid},
  } = useForm<DemoteSuperAdminFormValues>({
    mode: 'onSubmit',
    defaultValues,
    resolver: zodResolver(schema),
  });

  if (target === null) return null;

  if (target.hasInstanceMembership) {
    const handleConfirm = async () => {
      try {
        await demoteSuperAdmin({accountId: target.accountId}).unwrap();
        setAlert(
          `${target.email} has been removed from super admin.`,
          'success'
        );
        onDemoted?.();
        onClose();
      } catch (error) {
        setAlert(parseErrorMessage(error), 'error');
        onClose();
      }
    };

    return (
      <ConfirmDialog
        opened={true}
        onClose={onClose}
        onConfirm={handleConfirm}
        title="Remove Super Admin?"
        body={`Remove super admin access from ${target.email}? They will retain their existing instance memberships.`}
        confirmationButtonColor="red"
        buttonTextYes="Remove"
        buttonTextNo="Cancel"
        confirmationButtonDisabled={isLoading}
      />
    );
  }

  const handleSubmit = async (values: DemoteSuperAdminFormValues) => {
    try {
      await demoteSuperAdmin({
        accountId: target.accountId,
        targetInstanceId: Number(values.targetInstanceId),
        roleId: Number(values.roleId) as RoleEnum,
      }).unwrap();
      setAlert(
        `${target.email} has been removed from super admin.`,
        'success'
      );
      reset();
      onDemoted?.();
      onClose();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      onClose();
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title="Remove Super Admin"
      size="md"
      styles={{title: {fontWeight: 700}}}>
      <form onSubmit={handleFormSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            <strong>{target.email}</strong> has no instance membership. Select
            an instance and role to assign after removing super admin access.
          </Text>
          <Controller
            name="targetInstanceId"
            control={control}
            render={({field, fieldState}) => (
              <Select
                label="Target Instance"
                placeholder="Select an instance"
                required
                data={instanceOptions}
                searchable
                value={field.value || null}
                onChange={val => field.onChange(val ?? '')}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="roleId"
            control={control}
            render={({field, fieldState}) => (
              <Select
                label="Role"
                placeholder="Select a role"
                required
                data={ROLE_OPTIONS}
                value={field.value || null}
                onChange={val => field.onChange(val ?? '')}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
              />
            )}
          />
          <Divider />
          <Group justify="flex-end">
            <DialogButton size="sm" onClick={onClose}>
              Cancel
            </DialogButton>
            <DeleteButton
              size="sm"
              type="submit"
              disabled={!isValid}
              loading={isLoading}>
              Remove
            </DeleteButton>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
